import { DatePicker, Form, Modal } from "antd";
import { FormController, TFormStructure } from "formifex";
import { useContext, useEffect, useMemo, useRef } from "react";
import { capitalizeFirstLetter } from "../../utils/string_convert_helper";
import { getProjectMembers } from "../../utils/utils";
import useAllProjects from "../../hooks/useAllProjects";
import { useUserStore } from "../../stores/userStore";
import FormBuilderCancelButton from "../Button/FormBuilderCancelButton";
import FormBuilderSaveButton from "../Button/FormBuilderSaveButton";
import DateRangePicker from "../Date/DateRangePicker";
import TreeForm from "../TreeForm";
import {
	generateExcelReport,
	generateIndividualTimesheetData,
	generateTimesheetPDFData,
} from "../../services/api-server/timewriting";
import { HomeContext } from "../../containers/Home";
import { pdf } from "@react-pdf/renderer";
import { TimesheetPDF } from "../PDF/PDFViewer";
import Emitter from "../../utils/emitter";
import dayjs from "dayjs";

interface ExportReportModalProps {
	open: boolean;
	onClose: () => void;
	exportType: "excel" | "pdf" | null;
	defaultCurrentUser?: boolean;
	openCalendar?: boolean;
	activeProject?: any;
}

// Timesheet status options
const TIMESHEET_STATUS_OPTIONS = [
	{ name: "Draft", value: "updated" },
	{ name: "Submitted", value: "submitted" },
	{ name: "Approved", value: "approved" },
];

function ExportReportModal(props: ExportReportModalProps) {
	const form = FormController.useFormController();
	const { userInfo, allTenants }: any = useContext(HomeContext);

	// Fetch all tenant projects
	const { data: allProjects } = useAllProjects() as { data: any[] | undefined };

	// Build two derived structures from allProjects:
	// 1. projectsForTree  – { tenantKey: [name1, name2, ...] } for TreeForm (string arrays)
	// 2. projectValueMap   – { "tenantKey-name": projectObject } reverse lookup
	const { projectsForTree, projectValueMap } = useMemo(() => {
		const forTree: Record<string, string[]> = {};
		const valueMap: Record<string, any> = {};

		if (!allProjects?.length)
			return { projectsForTree: forTree, projectValueMap: valueMap };

		allProjects.forEach((project: any) => {
			const tenantKey = project.tenant_key || "unknown";
			const displayName = project.project_name;

			if (!forTree[tenantKey]) forTree[tenantKey] = [];
			forTree[tenantKey].push(displayName);

			// TreeForm builds composite values as `${tenantKey}-${displayName}`
			const treeValue = `${tenantKey}-${displayName}`;
			valueMap[treeValue] = project;
		});

		return { projectsForTree: forTree, projectValueMap: valueMap };
	}, [allProjects]);

	// Build members-by-role tree from activeProject for PDF + openCalendar mode
	const membersForTree = useMemo(() => {
		if (!props.activeProject) return {};
		const project = props.activeProject;
		const roleMembers: Record<string, string[]> = {};

		if (project.project_manager?.length > 0) {
			roleMembers["Project Manager"] = [...project.project_manager];
		}

		project.role_assignments?.forEach((role: any) => {
			const roleName = role.role;
			if (!roleMembers[roleName]) roleMembers[roleName] = [];
			if (Array.isArray(role.appointee)) {
				roleMembers[roleName].push(...role.appointee);
			} else if (role.appointee) {
				roleMembers[roleName].push(role.appointee);
			}
		});

		if (project.general_members?.length > 0) {
			roleMembers["General Member"] = [...project.general_members];
		}

		return roleMembers;
	}, [props.activeProject]);

	// Watch selected project composite keys from the form (e.g. "TENANT_KEY-projectName")
	const selectedProjectKeys: string[] | undefined = Form.useWatch(
		"projects",
		form.formRef
	);

	// Derive unique user emails from selected projects only
	const userOptions = useMemo(() => {
		if (!selectedProjectKeys?.length) return [];

		const selectedProjects = selectedProjectKeys
			.map((key: string) => projectValueMap[key])
			.filter(Boolean);

		const relatedProjectsMembers = selectedProjects.flatMap((project: any) =>
			getProjectMembers(project)
		);
		const uniqueMembers = Array.from(new Set(relatedProjectsMembers));
		return uniqueMembers.map((member: any) => ({
			value: member,
			name: member,
		}));
	}, [selectedProjectKeys, projectValueMap]);

	// Use a ref for the dataObject so render closures always read the latest
	// data without needing to rebuild the schema (which would reset the form).
	const dataObjectRef = useRef<any>({
		projects: [],
		users: [],
		members: {},
		statuses: TIMESHEET_STATUS_OPTIONS,
	});

	// Keep the ref in sync with the latest data on every render
	dataObjectRef.current = {
		projects: projectsForTree,
		users: userOptions,
		members: membersForTree,
		statuses: TIMESHEET_STATUS_OPTIONS,
	};

	// Build the form schema based on export type and context:
	// - Excel + defaultCurrentUser: Projects, Date Range, Timesheet Status
	// - Excel + !defaultCurrentUser: Projects, Users, Date Range, Timesheet Status
	// - PDF + defaultCurrentUser: Projects, Month
	// - PDF + !defaultCurrentUser + openCalendar: Members, Month
	// (PDF + !defaultCurrentUser + !openCalendar is never shown — PDF removed from dropdown)
	const schema: TFormStructure = useMemo(() => {
		const items: TFormStructure["items"] = [];
		const isPdf = props.exportType === "pdf";

		// --- Projects field ---
		// Shown for: all Excel modes, PDF + defaultCurrentUser
		if (!isPdf || props.defaultCurrentUser) {
			items.push({
				id: "projects",
				type: "custom",
				render: () => (
					<TreeForm
						label="Projects"
						name="projects"
						dataObject={dataObjectRef.current}
						selectAllLabel="All projects from all tenants"
						required={true}
					/>
				),
			});
		}

		// --- Members field ---
		// Shown for: PDF + !defaultCurrentUser + openCalendar
		if (isPdf && !props.defaultCurrentUser && props.openCalendar) {
			items.push({
				id: "members",
				type: "custom",
				render: () => (
					<TreeForm
						label="Members"
						name="members"
						dataObject={dataObjectRef.current}
						selectAllLabel="All Members"
						required={true}
					/>
				),
			});
		}

		// --- Users field ---
		// Shown for: Excel + !defaultCurrentUser
		if (!isPdf && !props.defaultCurrentUser) {
			items.push({
				id: "users",
				type: "custom",
				render: () => (
					<TreeForm
						label="Users"
						name="users"
						dataObject={dataObjectRef.current}
						selectAllLabel="All Users"
						required={true}
					/>
				),
			});
		}

		// --- Date field ---
		if (isPdf) {
			// Month picker for PDF
			items.push({
				id: "month",
				type: "custom",
				render: () => (
					<Form.Item
						label="Month"
						name="month"
						rules={[{ required: true, message: "Please select a month" }]}
					>
						<DatePicker
							picker="month"
							format="MMM YYYY"
							style={{ width: "100%" }}
						/>
					</Form.Item>
				),
			});
		} else {
			// Date Range for Excel
			items.push({
				id: "date_range",
				type: "custom",
				render: () => (
					<Form.Item
						label="Date Range"
						name="date_range"
						rules={[{ required: true, message: "Please select a date range" }]}
					>
						<DateRangePicker />
					</Form.Item>
				),
			});
		}

		// --- Timesheet Status ---
		// Shown for: all Excel modes only
		if (!isPdf) {
			items.push({
				id: "statuses",
				type: "custom",
				render: () => (
					<TreeForm
						label="Timesheet Status"
						name="statuses"
						dataObject={dataObjectRef.current}
						selectAllLabel="All Status"
						required={true}
					/>
				),
			});
		}

		return {
			id: "export-report",
			requiredMark: "optional",
			layout: "vertical",
			items,
		};
	}, [props.exportType, props.openCalendar, props.defaultCurrentUser]);

	// Get current user email from the user store
	const currentUserEmail = useUserStore((state) => state.user?.email);

	// Set the schema when the modal opens or when the schema changes
	useEffect(() => {
		if (props.open) {
			form.setSchema(schema);
		}
	}, [props.open, schema]);

	const handleFormCancel = () => {
		props.onClose();
		form.formRef.resetFields();
	};

	const handleFormSave = () => {
		const formData = { ...form.formData };

		if (props.exportType === "excel") {
			// --- Excel export ---
			if (props.defaultCurrentUser && currentUserEmail) {
				formData.users = [currentUserEmail];
			}

			// Transform projects from tree values to grouped object
			if (Array.isArray(formData.projects)) {
				const grouped: Record<string, string[]> = {};
				formData.projects.forEach((treeValue: string) => {
					const project = projectValueMap[treeValue];
					if (project) {
						const tenantKey = project.tenant_key || "unknown";
						if (!grouped[tenantKey]) grouped[tenantKey] = [];
						grouped[tenantKey].push(project._id);
					}
				});
				formData.projects = grouped;
			}

			formData.generated_by = currentUserEmail;
			formData.generated_on = new Date().toISOString();

			// Convert date_range to UTC while preserving the date (not converting timezone)
			if (formData.date_range && Array.isArray(formData.date_range)) {
				formData.date_range = formData.date_range.map((date: any) =>
					dayjs.utc(dayjs(date).format("YYYY-MM-DD")).toISOString()
				);
			}

			generateExcelReport(formData);
		} else if (props.exportType === "pdf") {
			// --- PDF export ---
			const month = dayjs(formData.month).format("MMMM YYYY");
			const firstDay = dayjs(formData.month)
				.startOf("month")
				.utc()
				.toISOString();
			const lastDay = dayjs(formData.month).endOf("month").utc().toISOString();

			// Close modal before async generation
			props.onClose();
			form.formRef.resetFields();

			if (props.defaultCurrentUser) {
				// Individual PDF: generateIndividualTimesheetData
				const projectIds = (formData.projects || [])
					.map((treeValue: string) => projectValueMap[treeValue]?._id)
					.filter(Boolean);

				console.log("handleFormSave individual pdf", {
					month,
					projectIds,
				});

				generateIndividualTimesheetData(
					month,
					projectIds,
					allTenants,
					firstDay,
					lastDay,
					userInfo?.user
				)
					.then(async (data: any) => {
						if (!data) {
							Emitter.emit("alert", {
								type: "alert",
								message: "Timesheet data not found.",
								description: "",
								top: true,
								closeable: false,
								timeout: 3000,
							});
							return;
						}
						const blob = await pdf(
							<TimesheetPDF
								data={data}
								month={month}
								type="individual-timesheet-report"
							/>
						).toBlob();
						const url = URL.createObjectURL(blob);
						const link = document.createElement("a");
						link.href = url;
						link.download = `${userInfo?.user?.name} - ${month}.pdf`;
						link.click();
						URL.revokeObjectURL(url);
					})
					.catch(() => {
						Emitter.emit("alert", {
							type: "alert",
							message: "Failed to fetch individual timesheet data.",
							description: "",
							top: true,
							closeable: false,
							timeout: 3000,
						});
					});
			} else if (props.openCalendar && props.activeProject) {
				// Monthly PDF: generateTimesheetPDFData
				const member_roles: Record<string, string[]> = {};
				if (formData.members) {
					for (const item of formData.members) {
						const [parent, child] = item.split("-");
						if (!member_roles[parent]) member_roles[parent] = [];
						member_roles[parent].push(child);
					}
				}

				console.log("handleFormSave monthly pdf", {
					month,
					member_roles,
				});

				generateTimesheetPDFData(
					props.activeProject.tenant_key,
					props.activeProject._id,
					month,
					member_roles,
					firstDay,
					lastDay,
					userInfo?.user
				)
					.then(async (data: any) => {
						if (!data) return;
						const blob = await pdf(
							<TimesheetPDF
								data={data}
								month={month}
								type="monthly-timesheet-report"
							/>
						).toBlob();
						const url = URL.createObjectURL(blob);
						const link = document.createElement("a");
						link.href = url;
						link.download = `${data.project.project_name} - ${month}.pdf`;
						link.click();
						URL.revokeObjectURL(url);
					})
					.catch(() => {
						Emitter.emit("alert", {
							type: "alert",
							message: "Failed to fetch timesheet data.",
							description: "",
							top: true,
							closeable: false,
							timeout: 3000,
						});
					});
			}
		}
	};

	const getModalTitle = () => {
		const type = capitalizeFirstLetter(props.exportType ?? "") || "";
		return `Export ${type} Report`;
	};

	return (
		<Modal
			closable={false}
			title={getModalTitle()}
			open={props.open}
			centered
			footer={() => {
				return (
					<>
						<FormBuilderCancelButton
							form={form}
							handleFormCancel={handleFormCancel}
							alwaysEnabled
						/>
						<FormBuilderSaveButton
							form={form}
							handleFormSave={handleFormSave}
							labelText="Export"
						/>
					</>
				);
			}}
		>
			<FormController
				formController={form}
				resetOnDependencyChange={true}
				resetOnDependencyEmpty={true}
			/>
		</Modal>
	);
}

export default ExportReportModal;
